import { useState, useEffect, useCallback } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";
import type {
  IncomeStatementResponse,
  IncomeStatementQueryParams,
} from "../types";

interface UseIncomeStatementResult {
  report: IncomeStatementResponse | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch Income Statement report.
 *
 * Returns null if fiscalYearId is not provided.
 */
export function useIncomeStatement(
  params: Partial<IncomeStatementQueryParams>,
): UseIncomeStatementResult {
  const [report, setReport] = useState<IncomeStatementResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchKey, setFetchKey] = useState(0);

  const { fiscalYearId, includeZeroBalances } = params;

  useEffect(() => {
    // Don't fetch if fiscalYearId is not provided
    if (!fiscalYearId) {
      setReport(null);
      setIsLoading(false);
      return;
    }

    const fetchReport = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const queryParams = new URLSearchParams();
        queryParams.set("fiscalYearId", fiscalYearId);
        if (includeZeroBalances) {
          queryParams.set("includeZeroBalances", "true");
        }

        const url = `/api/reports/income-statement?${queryParams.toString()}`;
        const data = await apiFetch.get<IncomeStatementResponse>(url);
        setReport(data);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err);
        } else {
          setError(err instanceof Error ? err : new Error("Unknown error"));
        }
        setReport(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [fetchKey, fiscalYearId, includeZeroBalances]);

  const refetch = useCallback(() => setFetchKey((k) => k + 1), []);

  return {
    report,
    isLoading,
    error,
    refetch,
  };
}
